import { previewNames } from "./data/preview-names";
import { type GameDestinationContext } from "@softmax/web-addresses";
import { type GamePageContext } from "@/features/game/state";
import {
  frontDoorPath,
  curatedCoworldName,
  curatedLeagueName,
} from "@softmax/web-features/coworlds";
import { theaterCorpus, theaterLeague } from "@/features/watch/data/index";
import { leagueTag } from "@/lib/leagues/watch/index";
import {
  slugifyLeagueName,
  leagueRoute,
  watchAddresses,
} from "@softmax/web-features/leagues";

import { type PreviewLeagueLink } from "./content/preview-competition.types";
export async function resolvePreviewLeague({
  signal,
  slug,
  context,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly slug: string;
  readonly context?: GamePageContext | undefined;
}) {
  const segments = slug.split("/");

  if (context?.gameId) segments.splice(0, 1, context.address.slug);

  const corpusSlug = segments.join("/");
  const [catalog, selectedLeague] = await Promise.all([
    theaterCorpus({ signal, slug: corpusSlug, context: context ?? null }),
    context?.league
      ? theaterLeague({
          signal,
          id: context.league.id,
          isPublic: context.league.public,
        })
      : null,
  ]);
  const corpus = catalog.leagues;

  if (!corpus) {
    throw new Error(
      `preview: the corpus is unavailable, so "${slug}" cannot be resolved`,
    );
  }

  const [, leagueAddress, ...extraSegments] = corpusSlug.split("/");
  const slashIndex = corpusSlug.indexOf("/");
  const coworldAddress =
    slashIndex === -1 ? corpusSlug : corpusSlug.slice(0, slashIndex);
  const addresses = watchAddresses(corpus);

  if (extraSegments.length) return null;

  const catalogLeague = leagueAddress
    ? addresses.currentLeagueOf(corpusSlug)
    : (addresses.leagueOf(coworldAddress) ??
      addresses.leagueOf(slugifyLeagueName(coworldAddress)));

  if (!catalogLeague) return null;

  const league =
    selectedLeague ??
    (await theaterLeague({
      signal,
      id: catalogLeague.id,
      isPublic: catalogLeague.public !== false,
    }));
  const leagueDestination = leagueRoute(league);
  const leagueSegment = leagueAddress ? leagueDestination.league : undefined;
  const route: GameDestinationContext = leagueSegment
    ? leagueDestination
    : { game: leagueDestination.game };
  const coworldSlug = slugifyLeagueName(
    league.game.coworld_name ?? league.game.name,
  );
  const coworldLeagues = corpus.filter((l) => l.game.id === league.game.id);
  const leagueCount = coworldLeagues.length;
  const leagueCandidates = coworldLeagues.map((candidate) => {
    const siblingRoute = leagueRoute(candidate);
    return {
      name: curatedLeagueName(candidate),
      href: frontDoorPath({
        corpusSlug: slugifyLeagueName(
          candidate.game.coworld_name ?? candidate.game.name,
        ),
        route: siblingRoute,
      }),
      current: candidate.id === league.id,
      isRoot: candidate.id === candidate.game.default_league_id,
    };
  });
  const leagues: PreviewLeagueLink[] = leagueCandidates
    .toSorted(
      (a, b) =>
        Number(b.isRoot) - Number(a.isRoot) || a.name.localeCompare(b.name),
    )
    .map((l) => ({
      name: l.name,
      href: l.href,
      current: l.current,
    }));
  const curatedName = previewNames[coworldSlug];
  const coworldName =
    curatedName?.name ??
    curatedCoworldName(coworldSlug) ??
    (league.game.name?.trim() || leagueTag(league));
  const name = leagueSegment ? curatedLeagueName(league) : coworldName;
  return {
    league,
    route,
    leagueSegment,
    coworldSlug,
    coworldLeagues,
    leagueCount,
    leagues,
    curatedName,
    coworldName,
    name,
    corpus,
    corpusSlug,
    addresses,
    viewer: catalog.viewer,
  };
}
export type ResolvedPreviewLeague = NonNullable<
  Awaited<ReturnType<typeof resolvePreviewLeague>>
>;
